import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {OfficeBuildingIcon, UserIcon} from "@heroicons/react/outline";
// import {useNavigate} from "react-router-dom";
import useSorting from "../../../hooks/useSorting";
import {SortDirection} from "../../../types/SortDirection";
import {useAuth} from "../../../context/AuthContext";
import {useOperator} from "../../../context/OperatorContext";
import {Building} from "../../../types/Building";
import BuildingContextMenu from "../../../components/menus/BuildingContextMenu";
import DeleteBuilding from "../Delete";
import FeatureFlag from "../../../types/FeatureFlag";
import NotAuthorised from "../../NotAuthorised";

export default function BuildingList() {
  // const navigate = useNavigate();
  const auth = useAuth();
  const {termFor} = useOperator();

  const {currentSort, queryString: sort, sortBy} = useSorting();
  const [page, setPage] = useState<number>(1);

  const buildings = useQuery(
    ['buildings.all', {sort, page}],
    () => ngage.buildings.filtered({sort, page}),
    {keepPreviousData: true, enabled: auth.can('buildings:read')}
  )

  const [deletedBuilding, setDeletedBuilding] = useState<Building | null>(null);

  // Prefetch next and previous pages
  prefetch('buildings', {sort, page}, ngage.buildings.filtered);

  const breadcrumbs = {'Operations': '/', [termFor('Buildings')] : '/buildings'}
  // const actions = <Button icon={PlusIcon} onClick={() => navigate('/buildings/new')}>New</Button>

  // Prevent unauthorised viewing
  if (auth.cannot('buildings:read') || auth.cannotUse(FeatureFlag.IndoorMapping)) {
    return <NotAuthorised/>
  }

  // Prepare meta data
  const meta = [
    {icon: OfficeBuildingIcon, value: buildings?.data ? `${buildings?.data?.data.length.toLocaleString()} registered` : ''}
  ]

  return (
    <Page title="Buildings" breadcrumbs={breadcrumbs} actions={null} meta={meta}>
      <Table paginationData={buildings.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader
              sortable
              sortDirection={currentSort.name ?? SortDirection.NONE}
              onSort={direction => sortBy('name', direction)}
            >
              Name
            </TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader>Zone</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={buildings.isLoading}>
          {buildings?.data && buildings.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={8}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <UserIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">{`No ${termFor('buildings')}`}</span>
                </div>
              </TableData>
            </TableRow>
            : buildings?.data?.data.map((building, index) => (
              <TableRow key={building.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <BuildingContextMenu
                        building={building}
                        onDelete={() => setDeletedBuilding(building)}
                        deleteDisabled
                        position="left"
                      />

                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      {building.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{building.description}</TableData>
                <TableData>{building.zone.name}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <BuildingContextMenu
                    building={building}
                    onDelete={() => setDeletedBuilding(building)}
                    deleteDisabled
                  />
                </TableData>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {/* Delete confirmation modal */}
      <DeleteBuilding
        building={deletedBuilding}
        show={!!deletedBuilding}
        onCancel={() => setDeletedBuilding(null)}
        onDelete={() => {
          setDeletedBuilding(null);
          buildings?.refetch();
        }}
        onDeleteFail={() => setDeletedBuilding(null)}
      />
    </Page>
  );
}